import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Box,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Divider,
  useTheme,
} from '@mui/material';
import {
  CheckCircle as ConnectedIcon,
  Cancel as DisconnectedIcon,
  Error as ErrorIcon,
  Sync as SyncIcon,
  Cable as ConnectIcon,
  LinkOff as DisconnectIcon,
  Speed as TestIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { fetchIntegrations, connectIntegration, disconnectIntegration, testIntegration, syncIntegration } from '../services/api';

const STATUS_META = {
  connected: { label: 'Connected', color: 'success', Icon: ConnectedIcon },
  disconnected: { label: 'Not Connected', color: 'default', Icon: DisconnectedIcon },
  error: { label: 'Error', color: 'error', Icon: ErrorIcon },
  pending: { label: 'Pending', color: 'warning', Icon: SyncIcon },
};

function StatusChip({ status }) {
  const meta = STATUS_META[status] || STATUS_META.disconnected;
  const Icon = meta.Icon;
  return (
    <Chip
      icon={<Icon fontSize="small" />}
      label={meta.label}
      color={meta.color}
      size="small"
      variant={status === 'connected' ? 'filled' : 'outlined'}
    />
  );
}

function IntegrationRow({ integration, busy, onConnect, onDisconnect, onTest, onSync }) {
  const { id, name, description, status, lastSync, requiresKey } = integration;
  const isConnected = status === 'connected';

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" component="div">
              {name}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {description}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <StatusChip status={status} />
              {lastSync && (
                <Typography variant="caption" color="text.secondary">
                  Last sync: {new Date(lastSync).toLocaleString()}
                </Typography>
              )}
            </Stack>
          </Box>
          <Stack direction="row" spacing={1}>
            {isConnected ? (
              <>
                <Tooltip title="Sync now">
                  <IconButton
                    size="small"
                    disabled={busy}
                    onClick={() => onSync(id)}
                    color="primary"
                  >
                    <SyncIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Test connection">
                  <IconButton
                    size="small"
                    disabled={busy}
                    onClick={() => onTest(id)}
                    color="info"
                  >
                    <TestIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  disabled={busy}
                  startIcon={busy ? <CircularProgress size={16} /> : <DisconnectIcon />}
                  onClick={() => onDisconnect(id)}
                >
                  Disconnect
                </Button>
              </>
            ) : (
              <Button
                size="small"
                variant="contained"
                disabled={busy}
                startIcon={busy ? <CircularProgress size={16} /> : <ConnectIcon />}
                onClick={() => onConnect(integration)}
              >
                Connect
              </Button>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function Integrations({ user }) {
  const theme = useTheme();
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [actionMsg, setActionMsg] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [apiKey, setApiKey] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchIntegrations();
      setIntegrations(Array.isArray(data) ? data : data?.integrations || []);
    } catch (err) {
      setError(err.message || 'Failed to load integrations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleConnectClick = (integration) => {
    if (integration.requiresKey) {
      setApiKey('');
      setDialog(integration);
    } else {
      doConnect(integration.id, {});
    }
  };

  const doConnect = async (id, config) => {
    setBusyId(id);
    setActionMsg(null);
    try {
      await connectIntegration(id, config);
      setActionMsg({ type: 'success', text: 'Integration connected.' });
      setDialog(null);
      await load();
    } catch (err) {
      setActionMsg({ type: 'error', text: err.message || 'Failed to connect.' });
    } finally {
      setBusyId(null);
    }
  };

  const handleDisconnect = async (id) => {
    setBusyId(id);
    setActionMsg(null);
    try {
      await disconnectIntegration(id);
      setActionMsg({ type: 'success', text: 'Integration disconnected.' });
      await load();
    } catch (err) {
      setActionMsg({ type: 'error', text: err.message || 'Failed to disconnect.' });
    } finally {
      setBusyId(null);
    }
  };

  const handleTest = async (id) => {
    setBusyId(id);
    setActionMsg(null);
    try {
      const res = await testIntegration(id);
      setActionMsg({
        type: 'success',
        text: res?.ok ? 'Connection test passed.' : 'Connection test returned issues.',
      });
    } catch (err) {
      setActionMsg({ type: 'error', text: err.message || 'Connection test failed.' });
    } finally {
      setBusyId(null);
    }
  };

  const handleSync = async (id) => {
    setBusyId(id);
    setActionMsg(null);
    try {
      await syncIntegration(id);
      setActionMsg({ type: 'success', text: 'Sync triggered.' });
      await load();
    } catch (err) {
      setActionMsg({ type: 'error', text: err.message || 'Sync failed.' });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Box sx={{ maxWidth: 820, mx: 'auto', p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Integrations
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage connected services for {user?.email || 'your account'}.
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={load} disabled={loading} color="primary">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      {actionMsg && (
        <Alert
          severity={actionMsg.type}
          sx={{ mb: 2 }}
          onClose={() => setActionMsg(null)}
        >
          {actionMsg.text}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : integrations.length === 0 ? (
        <Alert severity="info">No integrations available.</Alert>
      ) : (
        integrations.map((integration) => (
          <IntegrationRow
            key={integration.id}
            integration={integration}
            busy={busyId === integration.id}
            onConnect={handleConnectClick}
            onDisconnect={handleDisconnect}
            onTest={handleTest}
            onSync={handleSync}
          />
        ))
      )}

      <Dialog open={Boolean(dialog)} onClose={() => setDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Connect {dialog?.name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the API key for {dialog?.name}. It will be stored securely and used for
            authenticated requests.
          </Typography>
          <TextField
            label="API Key"
            type="password"
            fullWidth
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!apiKey.trim() || busyId === dialog?.id}
            onClick={() => doConnect(dialog.id, { apiKey: apiKey.trim() })}
          >
            Connect
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
